import { useCallback, useMemo, useState, type FormEventHandler } from "react";
import { useSearchParams } from "react-router-dom";
import { SearchX } from "lucide-react";
import type { GroupSearchQuery } from "../types";
import { getApi } from "../api";
import { useAsyncResource } from "../hooks/useAsyncResource";
import { usePageSetup } from "../hooks/usePageSetup";
import { useShortcuts } from "../hooks/useShortcuts";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Pagination } from "../components/Pagination";
import { EmptyState, ErrorState } from "../components/ScreenStates";
import { DateInput, SelectInput, TextInput } from "../components/fields";
import { GroupsGrid } from "../components/groups/GroupsGrid";
import { useGroupRowActions } from "../components/groups/useGroupRowActions";

const DEFAULT_SORT = { key: "createdUtc", direction: "desc" } as const;
const PAGE_SIZE = 20;

interface SearchFilters {
  name: string;
  effectiveFrom: string;
  effectiveTo: string;
  underwriterUserId: string;
  quoteOwnerName: string;
}

function filtersFromParams(params: URLSearchParams): SearchFilters {
  return {
    name: params.get("name") ?? "",
    effectiveFrom: params.get("effectiveFrom") ?? "",
    effectiveTo: params.get("effectiveTo") ?? "",
    underwriterUserId: params.get("underwriter") ?? "",
    quoteOwnerName: params.get("quoteOwner") ?? "",
  };
}

function toQuery(filters: SearchFilters, page: number): GroupSearchQuery {
  return {
    name: filters.name || undefined,
    effectiveFrom: filters.effectiveFrom || undefined,
    effectiveTo: filters.effectiveTo || undefined,
    underwriterUserId: filters.underwriterUserId || undefined,
    quoteOwnerName: filters.quoteOwnerName || undefined,
    page,
    pageSize: PAGE_SIZE,
  };
}

/** Filters persist in the URL so results are shareable and survive refresh. */
export function SearchGroupsPage() {
  usePageSetup("Search for Group", null);
  const [params, setParams] = useSearchParams();
  const applied = useMemo(() => filtersFromParams(params), [params]);
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);
  const [draft, setDraft] = useState<SearchFilters>(applied);
  const lookups = useAsyncResource(async () => ({ users: await getApi().listUsers(), quoteOwners: await getApi().listQuoteOwners() }), "lookups", "SearchGroupsPage.lookups");
  const results = useAsyncResource(() => getApi().searchGroups(toQuery(applied, page)), params.toString(), "SearchGroupsPage.search");
  const rowActions = useGroupRowActions(() => results.reload());

  const setPage = useCallback(
    (next: number) => {
      const nextParams = new URLSearchParams(params);
      nextParams.set("page", String(next));
      setParams(nextParams);
    },
    [params, setParams],
  );

  const totalPages = Math.max(1, Math.ceil((results.data?.totalCount ?? 0) / PAGE_SIZE));
  const shortcuts = useMemo(
    () => ({ next: page < totalPages ? () => setPage(page + 1) : undefined, previous: page > 1 ? () => setPage(page - 1) : undefined }),
    [page, totalPages, setPage],
  );
  useShortcuts(shortcuts);

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    const nextParams = new URLSearchParams();
    if (draft.name) nextParams.set("name", draft.name);
    if (draft.effectiveFrom) nextParams.set("effectiveFrom", draft.effectiveFrom);
    if (draft.effectiveTo) nextParams.set("effectiveTo", draft.effectiveTo);
    if (draft.underwriterUserId) nextParams.set("underwriter", draft.underwriterUserId);
    if (draft.quoteOwnerName) nextParams.set("quoteOwner", draft.quoteOwnerName);
    setParams(nextParams);
  };

  const handleClear = () => {
    setDraft({ name: "", effectiveFrom: "", effectiveTo: "", underwriterUserId: "", quoteOwnerName: "" });
    setParams(new URLSearchParams());
  };

  const userOptions = [{ value: "", label: "Any underwriter" }, ...(lookups.data?.users.map((user) => ({ value: user.id, label: user.displayName })) ?? [])];
  const ownerOptions = [{ value: "", label: "Any quote owner" }, ...(lookups.data?.quoteOwners.map((owner) => ({ value: owner, label: owner })) ?? [])];
  const { state } = results;

  return (
    <>
      <PageHeader title="Search for Group" subtitle="Combine filters, then press Enter or Search." />
      <Card title="Filters" className="mb-5">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-5">
          <TextInput id="search-name" label="Group Name" value={draft.name} onChange={(name) => setDraft({ ...draft, name })} placeholder="Contains..." />
          <DateInput id="search-from" label="Effective Date From" value={draft.effectiveFrom} onChange={(effectiveFrom) => setDraft({ ...draft, effectiveFrom })} />
          <DateInput id="search-to" label="Effective Date To" value={draft.effectiveTo} onChange={(effectiveTo) => setDraft({ ...draft, effectiveTo })} />
          <SelectInput id="search-underwriter" label="Underwriter" value={draft.underwriterUserId} onChange={(underwriterUserId) => setDraft({ ...draft, underwriterUserId })} options={userOptions} />
          <SelectInput id="search-owner" label="Quote Owner" value={draft.quoteOwnerName} onChange={(quoteOwnerName) => setDraft({ ...draft, quoteOwnerName })} options={ownerOptions} />
          <div className="flex gap-2 md:col-span-5">
            <Button type="submit">Search</Button>
            <Button variant="secondary" onClick={handleClear}>
              Clear
            </Button>
          </div>
        </form>
      </Card>
      {state.kind === "error" && <ErrorState message={state.message} onRetry={results.reload} />}
      {state.kind !== "error" && (
        <>
          <p className="mb-2 text-right text-sm text-gray-600" aria-live="polite">
            {state.data ? `${state.data.totalCount} result${state.data.totalCount === 1 ? "" : "s"}` : "Searching..."}
          </p>
          <GroupsGrid
            caption="Group search results"
            groups={state.data?.items ?? []}
            isLoading={state.kind === "loading"}
            defaultSort={DEFAULT_SORT}
            emptyState={<EmptyState icon={SearchX} message="No groups match these filters. Clear a filter or widen the date range." />}
            onEdit={rowActions.handleEdit}
            onCopy={rowActions.handleCopy}
            onDelete={(group) => void rowActions.handleDelete(group)}
          />
          {state.data && <Pagination page={page} pageSize={PAGE_SIZE} totalCount={state.data.totalCount} onPageChange={setPage} />}
        </>
      )}
    </>
  );
}
